<template>
  <div class="estruturacao-page">
    <EstruturacaoHubHeader />

    <section class="estruturacao-hero" :aria-label="t('estruturacao.heroAlt')">
      <div class="estruturacao-hero__inner">
        <div class="estruturacao-hero__copy">
          <p class="estruturacao-hero__eyebrow">
            <span class="estruturacao-hero__eyebrow-dot" aria-hidden="true" />
            {{ t('estruturacao.heroEyebrow') }}
          </p>
          <h2 class="estruturacao-hero__title">
            {{ t('estruturacao.heroTitleLine1') }}
            <span class="estruturacao-hero__title-accent">{{ t('estruturacao.heroTitleLine2') }}</span>
          </h2>
          <p class="estruturacao-hero__areas" :aria-label="t('estruturacao.heroAreasLabel')">
            <span v-for="(area, idx) in heroAreas" :key="area">
              <span class="estruturacao-hero__area">{{ area }}</span>
              <span v-if="idx < heroAreas.length - 1" class="estruturacao-hero__area-sep" aria-hidden="true">·</span>
            </span>
          </p>
          <p class="estruturacao-hero__desc">
            {{ t('estruturacao.heroDesc1') }}<span class="estruturacao-hero__desc-highlight">{{ t('estruturacao.heroDescMethodology') }}</span>{{ t('estruturacao.heroDesc2') }}<span class="estruturacao-hero__desc-highlight">{{ t('estruturacao.heroDescTechnology') }}</span>{{ t('estruturacao.heroDesc3') }}<span class="estruturacao-hero__desc-highlight">{{ t('estruturacao.heroDescGrowth') }}</span>{{ t('estruturacao.heroDesc4') }}
          </p>
          <div
            class="estruturacao-hero__scroll-hint"
            role="button"
            tabindex="0"
            @click="scrollToModules"
            @keydown.enter="scrollToModules"
            @keydown.space.prevent="scrollToModules"
          >
            <span class="estruturacao-hero__scroll-label">{{ t('estruturacao.heroCta') }}</span>
            <svg class="estruturacao-hero__scroll-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
          <p class="estruturacao-hero__note">{{ t('estruturacao.heroFootnote') }}</p>
        </div>

        <div class="estruturacao-hero__visual">
          <span class="estruturacao-hero__role" aria-hidden="true">{{ t('estruturacao.heroRoleLabel') }}</span>
          <img
            :src="heroImage"
            :alt="t('estruturacao.heroAlt')"
            class="estruturacao-hero__portrait"
          />
        </div>
      </div>
    </section>

    <div ref="modulesSectionRef" class="estruturacao-content">
      <div class="estruturacao-grid">
        <button
          v-for="section in ESTRUTURACAO_SECTIONS"
          :key="section.screenId"
          type="button"
          class="estruturacao-card"
          :class="{ 'estruturacao-card--locked': !hasAccess(section.screenId) }"
          :title="!hasAccess(section.screenId) ? t('estruturacao.locked') : t(section.labelKey)"
          @click="openSection(section)"
        >
          <img
            :src="section.image"
            :alt="t(section.labelKey)"
            class="estruturacao-card__image"
          />
          <div v-if="!hasAccess(section.screenId)" class="estruturacao-card__lock" aria-hidden="true">
            <svg class="estruturacao-card__lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <span class="sr-only">{{ t(section.labelKey) }}</span>
        </button>
      </div>

      <section class="estruturacao-access" :aria-label="t('estruturacao.accessTitle')">
        <div class="estruturacao-access__panel">
          <header class="estruturacao-access__header">
            <span class="estruturacao-access__eyebrow">{{ t('estruturacao.accessTitle') }}</span>
            <p class="estruturacao-access__subtitle">{{ t('estruturacao.accessSubtitle') }}</p>
          </header>

          <div class="estruturacao-access__links">
            <a
              :href="WHATSAPP_GROUP_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="estruturacao-access__link estruturacao-access__link--whatsapp"
            >
              <span class="estruturacao-access__icon-wrap estruturacao-access__icon-wrap--whatsapp">
                <svg class="estruturacao-access__icon" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </span>
              <span class="estruturacao-access__text">
                <span class="estruturacao-access__label">{{ t('estruturacao.accessWhatsapp') }}</span>
                <span class="estruturacao-access__hint">{{ t('estruturacao.accessWhatsappHint') }}</span>
              </span>
              <svg class="estruturacao-access__arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </a>

            <a
              :href="DISCORD_INVITE_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="estruturacao-access__link estruturacao-access__link--discord"
            >
              <span class="estruturacao-access__icon-wrap estruturacao-access__icon-wrap--discord">
                <svg class="estruturacao-access__icon" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 00-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 00-5.487 0 12.36 12.36 0 00-.617-1.23A.077.077 0 008.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 00-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 00.031.055 18.45 18.45 0 005.487 2.766.078.078 0 00.084-.026 13.83 13.83 0 001.226-1.963.074.074 0 00-.041-.104 12.321 12.321 0 01-1.872-.878.075.075 0 01-.008-.125c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.927 1.764 8.18 1.764 12.061 0a.074.074 0 01.078.009c.12.099.246.198.373.292a.075.075 0 01-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 00-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 00.084.028 18.46 18.46 0 005.493-2.766.076.076 0 00.032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 00-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z"/>
                </svg>
              </span>
              <span class="estruturacao-access__text">
                <span class="estruturacao-access__label">{{ t('estruturacao.accessDiscord') }}</span>
                <span class="estruturacao-access__hint">{{ t('estruturacao.accessDiscordHint') }}</span>
              </span>
              <svg class="estruturacao-access__arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import { useAuthStore } from '../stores/auth'
import { ESTRUTURACAO_SECTIONS, isEstruturacaoScreenId } from '../constants/estruturacaoSections'
import EstruturacaoHubHeader from '../components/estruturacao/EstruturacaoHubHeader.vue'
import { DISCORD_INVITE_URL, WHATSAPP_GROUP_URL } from '../config/supportContact'
import { useThemeStore } from '../stores/theme'
import heroDark from '../assets/estruturacao/édipo Oliveira.png'
import heroLight from '../assets/estruturacao/édipo Oliveira 2.png'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()

const heroImage = computed(() => (themeStore.isLight ? heroLight : heroDark))

const heroAreas = computed(() => [
  t('estruturacao.heroAreaCommercial'),
  t('estruturacao.heroAreaService'),
  t('estruturacao.heroAreaOperational'),
  t('estruturacao.heroAreaFinancial')
])

const modulesSectionRef = ref(null)

const scrollToModules = () => {
  modulesSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const screenPermissions = ref({})
const permissionsLoaded = ref(false)

const isAdminUser = () => {
  const roleName = authStore.user?.role?.nome
  return roleName === 'SUPER_ADMIN' || roleName === 'ADMIN'
}

const hasAccess = (screenId) => {
  if (isAdminUser()) return true
  if (!permissionsLoaded.value && isEstruturacaoScreenId(screenId)) return false
  if (screenPermissions.value[screenId] === undefined && isEstruturacaoScreenId(screenId)) return false
  return screenPermissions.value[screenId] === true
}

const loadScreenPermissions = async () => {
  try {
    const response = await api.get('/permissoes-tela/me')
    screenPermissions.value = response.data || {}
  } catch (error) {
    console.error('[ESTRUTURACAO] Erro ao carregar permissões:', error)
  } finally {
    permissionsLoaded.value = true
  }
}

const openSection = (section) => {
  if (!hasAccess(section.screenId)) {
    router.push({ path: '/acesso-negado', query: { tela: section.screenId } })
    return
  }
  router.push(section.route)
}

onMounted(loadScreenPermissions)
</script>

<style scoped>
.estruturacao-page {
  min-height: 100%;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
  --estrut-card-shadow-color: #191919;
}

.estruturacao-content {
  padding: 8px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
}

.estruturacao-access {
  width: 80%;
  max-width: 100%;
  box-sizing: border-box;
}

.estruturacao-access__panel {
  padding: 1.35rem 1.5rem 1.5rem;
  border-radius: 14px;
  border: 1px solid var(--border-primary);
  background:
    linear-gradient(180deg, color-mix(in srgb, #2563eb 6%, var(--bg-secondary)) 0%, var(--bg-secondary) 42%),
    var(--bg-secondary);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.04) inset,
    0 12px 40px rgba(15, 23, 42, 0.08);
}

.estruturacao-access__header {
  margin-bottom: 1.15rem;
  text-align: center;
}

.estruturacao-access__eyebrow {
  display: inline-block;
  margin-bottom: 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #60a5fa;
}

.estruturacao-access__subtitle {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: var(--text-secondary);
}

.estruturacao-access__links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.estruturacao-access__link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  text-decoration: none;
  overflow: hidden;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.estruturacao-access__link::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}

.estruturacao-access__link--whatsapp::before {
  background: linear-gradient(135deg, color-mix(in srgb, #25d366 14%, transparent) 0%, transparent 70%);
}

.estruturacao-access__link--discord::before {
  background: linear-gradient(135deg, color-mix(in srgb, #5865f2 16%, transparent) 0%, transparent 70%);
}

.estruturacao-access__link:hover {
  transform: translateY(-3px);
}

.estruturacao-access__link:hover::before {
  opacity: 1;
}

.estruturacao-access__link--whatsapp {
  border-color: color-mix(in srgb, #25d366 28%, var(--border-primary));
}

.estruturacao-access__link--whatsapp:hover {
  box-shadow: 0 14px 32px color-mix(in srgb, #25d366 22%, transparent);
}

.estruturacao-access__link--discord {
  border-color: color-mix(in srgb, #5865f2 28%, var(--border-primary));
}

.estruturacao-access__link--discord:hover {
  box-shadow: 0 14px 32px color-mix(in srgb, #5865f2 22%, transparent);
}

.estruturacao-access__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  flex-shrink: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}

.estruturacao-access__icon-wrap--whatsapp {
  background: linear-gradient(145deg, #34d399 0%, #059669 100%);
}

.estruturacao-access__icon-wrap--discord {
  background: linear-gradient(145deg, #818cf8 0%, #4f46e5 100%);
}

.estruturacao-access__icon {
  width: 1.45rem;
  height: 1.45rem;
}

.estruturacao-access__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.estruturacao-access__label {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.estruturacao-access__hint {
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.35;
}

.estruturacao-access__arrow {
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
  color: var(--text-tertiary);
  opacity: 0.55;
  transition: transform 0.25s ease, opacity 0.25s ease, color 0.25s ease;
}

.estruturacao-access__link:hover .estruturacao-access__arrow {
  opacity: 1;
  transform: translate(2px, -2px);
}

.estruturacao-access__link--whatsapp:hover .estruturacao-access__arrow {
  color: #25d366;
}

.estruturacao-access__link--discord:hover .estruturacao-access__arrow {
  color: #5865f2;
}

.estruturacao-hero {
  --hero-accent: #2563eb;
  --hero-accent-soft: #60a5fa;
  --hero-text: var(--text-primary, #f8fafc);
  --hero-text-muted: #94a3b8;
  --hero-cta-bg: #2563eb;
  --hero-cta-text: #ffffff;
  padding: 2rem 2.5rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.estruturacao-hero__inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 380px);
  gap: 2rem 3.5rem;
  align-items: center;
}

.estruturacao-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 1.1rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--hero-accent-soft) 88%, var(--hero-text-muted));
}

.estruturacao-hero__eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--hero-accent), var(--hero-accent-soft));
  box-shadow: 0 0 10px color-mix(in srgb, var(--hero-accent) 55%, transparent);
}

.estruturacao-hero__title {
  margin: 0 0 1rem;
  font-size: clamp(2rem, 3.8vw, 3.25rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: var(--hero-text);
}

.estruturacao-hero__title-accent {
  background: linear-gradient(135deg, var(--hero-accent) 0%, #7c3aed 55%, #ec4899 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.estruturacao-hero__areas {
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.6;
  color: var(--hero-text-muted);
}

.estruturacao-hero__area {
  color: color-mix(in srgb, var(--hero-accent-soft) 75%, var(--hero-text));
}

.estruturacao-hero__area-sep {
  margin: 0 0.45rem;
  opacity: 0.45;
}

.estruturacao-hero__desc {
  margin: 0 0 1.5rem;
  max-width: 34rem;
  font-size: 1.02rem;
  line-height: 1.7;
  color: var(--hero-text-muted);
}

.estruturacao-hero__desc-highlight {
  color: var(--hero-accent-soft);
  font-weight: 600;
}

.estruturacao-hero__scroll-hint {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 0.15rem;
  padding: 0;
  background: none;
  border: none;
  color: var(--hero-accent);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  user-select: none;
  transition: gap 0.2s ease, color 0.2s ease;
}

.estruturacao-hero__scroll-label {
  position: relative;
}

.estruturacao-hero__scroll-label::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--hero-accent), var(--hero-accent-soft));
  transform: scaleX(0.35);
  transform-origin: left center;
  transition: transform 0.22s ease;
}

.estruturacao-hero__scroll-hint:hover {
  gap: 14px;
  color: var(--hero-accent-soft);
}

.estruturacao-hero__scroll-hint:hover .estruturacao-hero__scroll-label::after {
  transform: scaleX(1);
}

.estruturacao-hero__scroll-hint:focus-visible {
  outline: 2px solid var(--hero-accent);
  outline-offset: 4px;
  border-radius: 4px;
}

.estruturacao-hero__scroll-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: var(--hero-accent-soft);
  animation: estrut-hero-scroll-bounce 1.8s ease-in-out infinite;
}

@keyframes estrut-hero-scroll-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

.estruturacao-hero__note {
  margin: 1rem 0 0;
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--hero-text-muted);
  opacity: 0.85;
}

.estruturacao-hero__visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 1.25rem;
}

.estruturacao-hero__role {
  position: absolute;
  left: -0.25rem;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--hero-accent-soft) 55%, var(--hero-text-muted));
  opacity: 0.85;
  user-select: none;
}

.estruturacao-hero__portrait {
  display: block;
  width: 100%;
  max-width: 360px;
  height: auto;
  border: none;
  user-select: none;
  -webkit-user-drag: none;
  filter: drop-shadow(0 14px 32px color-mix(in srgb, var(--hero-accent) 16%, transparent));
}

.estruturacao-grid {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  width: 80%;
  max-width: 100%;
  padding: 1.5rem 0.5rem;
  overflow: visible;
  box-sizing: border-box;
}

.estruturacao-card {
  --card-overlap: clamp(30px, 4.5vw, 20px);
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  margin-left: calc(var(--card-overlap) * -1);
  padding: 0;
  border: none;
  outline: none;
  background: none;
  background-color: transparent;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;
  overflow: visible;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.35s ease, z-index 0s;
  filter: none;
}

.estruturacao-card:focus,
.estruturacao-card:focus-visible {
  outline: none;
  border: none;
  box-shadow: none;
}

.estruturacao-card:first-child {
  margin-left: 0;
}

.estruturacao-card:nth-child(2) { z-index: 2; }
.estruturacao-card:nth-child(3) { z-index: 3; }
.estruturacao-card:nth-child(4) { z-index: 4; }

.estruturacao-card:hover {
  transform: scale(1.1);
  z-index: 10;
}

.estruturacao-grid:has(.estruturacao-card:hover) .estruturacao-card:not(:hover) {
  filter: grayscale(1) brightness(0.55);
}

.estruturacao-card--locked {
  cursor: not-allowed;
}

.estruturacao-card--locked:hover {
  transform: scale(1.04);
}

.estruturacao-card__image {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  transition: filter 0.3s ease;
}

.estruturacao-card:hover .estruturacao-card__image {
  filter:
    drop-shadow(0 0 3px var(--estrut-card-shadow-color))
    drop-shadow(0 8px 22px color-mix(in srgb, var(--estrut-card-shadow-color) 70%, transparent));
}

.estruturacao-card__lock {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 30%;
  background: transparent;
  border: none;
  color: #fff;
  pointer-events: none;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
}

.estruturacao-card__lock-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
}

.estruturacao-card--locked .estruturacao-card__image {
  filter: grayscale(0.5) brightness(0.7);
}

.estruturacao-card--locked:hover .estruturacao-card__image {
  filter:
    grayscale(0.5) brightness(0.7)
    drop-shadow(0 0 3px var(--estrut-card-shadow-color))
    drop-shadow(0 8px 22px color-mix(in srgb, var(--estrut-card-shadow-color) 70%, transparent));
}

.estruturacao-grid:has(.estruturacao-card:hover) .estruturacao-card--locked:not(:hover) .estruturacao-card__image {
  filter: grayscale(1) brightness(0.45);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1024px) {
  .estruturacao-grid {
    width: 100%;
  }

  .estruturacao-access {
    width: 100%;
  }

  .estruturacao-card__lock-icon {
    width: 2rem;
    height: 2rem;
  }
}

@media (max-width: 768px) {
  .estruturacao-hero {
    padding: 1.25rem 1rem 1rem;
  }

  .estruturacao-hero__inner {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }

  .estruturacao-hero__visual {
    order: -1;
    padding-left: 0;
  }

  .estruturacao-hero__role {
    left: 0;
  }

  .estruturacao-hero__areas {
    text-align: center;
  }

  .estruturacao-hero__scroll-hint {
    justify-content: center;
    width: 100%;
  }

  .estruturacao-hero__portrait {
    max-width: min(100%, 300px);
    margin: 0 auto;
  }

  .estruturacao-content {
    padding: 16px;
  }

  .estruturacao-grid {
    padding: 1rem 0 1.5rem;
  }

  .estruturacao-card {
    --card-overlap: clamp(22px, 6vw, 36px);
  }

  .estruturacao-card__lock-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .estruturacao-access__links {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
body.tema_clara .estruturacao-page {
  --estrut-card-shadow-color: #ffffff;
}

body.tema_clara .estruturacao-hero {
  --hero-accent: #1d4ed8;
  --hero-accent-soft: #2563eb;
  --hero-text: #0f172a;
  --hero-text-muted: #64748b;
  --hero-cta-bg: #2563eb;
}
</style>
